import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { timer, Observable, Subscription, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { catchError } from 'rxjs/operators';
import { FormGroup } from '@angular/forms';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Login } from '../interfaces/auth.inteface';
import { jwtDecode } from 'jwt-decode';
import { Code, Jwt } from '../interfaces/jwt.interface';
import { StorageService } from 'src/app/utils/storage.service';
import { AlertsService } from './alerts.service';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private url_back: string = environment.URL_BACK;

  private tokenExpirationSub?: Subscription;
  private tokenWarningSub?: Subscription;

  constructor(
    private httpClient: HttpClient,
    private jwtHelperService: JwtHelperService,
    private storage: StorageService,
    private alertService: AlertsService,
    private router: Router,
  ) { }

  /**
    * @author Ministerio de cultura
    * @createdate 2024-10-14
    * Metodo que retorna un posible error al llamado de una peticion HTTP.
    * @param error Informacion del error
  */
  handleError(error: HttpErrorResponse): Observable<never> {
    const dataError = { status: error.status, message: error.message };
    return throwError(() => dataError);
  }
  /**
    * @author Ministerio de cultura
    * @createdate 2024-10-14
    * Metodo que permite validar el inicio de sesion en el sistema. 
    * @param formData Informacion proveniente del formulario.  
  */
  login(formData: FormGroup, jwtExternal: string): Observable<Login> {
    return this.httpClient.post<Login>(`${this.url_back}login/auth`, formData.value, { headers: { Authorization: `Bearer ${jwtExternal}` } }).pipe(
      catchError(this.handleError)
    );
  }
  /**
    * @author Ministerio de cultura
    * @createdate 2024-10-27
    * Metodo que limpia el local storage y redirecciona al login. 
  */
  logout(): Observable<void> {
    return this.httpClient.put<void>(`${this.url_back}login/salir`, null).pipe(
      catchError(this.handleError)
    );
  }

  /**
    * @author Ministerio de cultura
    * @createdate 2026-02-02
    * Metodo que permite actualizar la contraseña y genera un correo con la respuesta 
  */
  resetPassword(form: FormGroup, jwtExternal: string) {
    return this.httpClient.post<{message: string, error: boolean}>(`${this.url_back}usuarios/change_password`, form, { headers: { Authorization: `Bearer ${jwtExternal}` } }).pipe(
      catchError(this.handleError)
    );
  }

  /**
    * @author Ministerio de cultura
    * @createdate 2024-10-14
    * Metodo que valida si el token del usuario es valido o no.
  */
  isUserAuthenticated(): boolean {
    const starageKey: string = 'token';
    const storageType = 'local';
    const token = this.storage.get<any>(starageKey, storageType);
    return token && !this.jwtHelperService.isTokenExpired(token);
  }
  /**
    * @author Ministerio de cultura
    * @createdate 2024-10-14
    * Metodo retorna el token decodificado.
  */
  decodeTokenUser(): Jwt {
    const starageKey: string = 'token';
    const storageType = 'local';
    const token = this.storage.get<any>(starageKey, storageType);
    const user: Jwt = jwtDecode(token);
    return user;
  }
  /**
    * @author Ministerio de cultura
    * @createdate 2024-10-26
    * Metodo retorna el token externo para la autenticacion.
  */
  getTokenExternal(): Observable<Code> {
    return this.httpClient.get<Code>(`${this.url_back}login/token`).pipe(
      catchError(this.handleError)
    );
  }

  startTokenExpirationWatcher(thresholdSeconds: number = 60): void {
    const token = localStorage.getItem('token');
    if (!token) return;

    const { exp } = jwtDecode<any>(token);
    const now = Math.floor(Date.now() / 1000);

    // 🔥 Si ya expiró
    if (exp <= now) {
      this.handleTokenExpired();
      return;
    }

    const msUntilExpire = (exp - now) * 1000;
    const msUntilWarning = Math.max((exp - now - thresholdSeconds) * 1000, 0);

    // Cancelar watchers anteriores
    this.tokenExpirationSub?.unsubscribe();
    this.tokenWarningSub?.unsubscribe();

    // 🔔 Advertencia
    this.tokenWarningSub = timer(msUntilWarning).subscribe(() => {
      this.alertService.showAlertNotification({
        title: '¡La sesión está pronta a expirar!',
        html: 'Cierre sesión e inicie nuevamente.',
        icon: 'warning',
        showConfirmButton: true,
        timer: 0
      });
    });

    // 🔥 Expiración real
    this.tokenExpirationSub = timer(msUntilExpire).subscribe(() => {
      this.handleTokenExpired();
    });
  }

  handleTokenExpired(): void {
    this.tokenExpirationSub?.unsubscribe();
    this.tokenWarningSub?.unsubscribe();
    this.storage.clear('local');
    this.storage.clear('session');
    this.router.navigate(['/']).then(() => {
      window.location.reload();
    });
  }

  clearTokenWatcher(): void {
    this.tokenExpirationSub?.unsubscribe();
    this.tokenWarningSub?.unsubscribe();
  }
}